import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  BarChart3, Users, GraduationCap, Code, Trophy, Briefcase, TrendingUp,
  ArrowLeft, Loader, MapPin, Zap, Target, Star, Award, Calendar
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NationalAnalyticsPage = () => {
  const [analytics, setAnalytics] = useState(null);
  const [trending, setTrending] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [analyticsRes, trendingRes] = await Promise.all([
        axios.get(`${API}/codearena/national-analytics`),
        axios.get(`${API}/codearena/trending`)
      ]);
      setAnalytics(analyticsRes.data);
      setTrending(trendingRes.data);
    } catch (err) {
      console.error('Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader className="animate-spin text-purple-400" size={32} />
      </div>
    );
  }

  const overview = analytics?.overview || {};

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <div className="border-b border-white/10 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <Link 
            to="/codearena" 
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6"
          >
            <ArrowLeft size={18} />
            Back to EvalentX Arena
          </Link>
          
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center">
              <BarChart3 className="text-white" size={28} />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">National Analytics</h1>
              <p className="text-gray-400 mt-1">Platform-wide statistics and insights</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Overview Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-5 text-center">
            <Users className="mx-auto text-purple-400 mb-2" size={28} />
            <p className="text-2xl font-bold text-white">{overview.total_students?.toLocaleString()}</p>
            <p className="text-xs text-gray-500">Developers</p>
          </div>
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-5 text-center">
            <GraduationCap className="mx-auto text-indigo-400 mb-2" size={28} />
            <p className="text-2xl font-bold text-white">{overview.total_colleges}</p>
            <p className="text-xs text-gray-500">Colleges</p>
          </div>
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-5 text-center">
            <Code className="mx-auto text-green-400 mb-2" size={28} />
            <p className="text-2xl font-bold text-white">{overview.total_problems_solved?.toLocaleString()}</p>
            <p className="text-xs text-gray-500">Problems Solved</p>
          </div>
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-5 text-center">
            <Trophy className="mx-auto text-amber-400 mb-2" size={28} />
            <p className="text-2xl font-bold text-white">{overview.total_contests}</p>
            <p className="text-xs text-gray-500">Contests</p>
          </div>
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-5 text-center">
            <Briefcase className="mx-auto text-emerald-400 mb-2" size={28} />
            <p className="text-2xl font-bold text-white">{overview.total_jobs}</p>
            <p className="text-xs text-gray-500">Active Jobs</p>
          </div>
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-5 text-center">
            <Target className="mx-auto text-cyan-400 mb-2" size={28} />
            <p className="text-2xl font-bold text-white">{overview.total_companies}</p>
            <p className="text-xs text-gray-500">Companies</p>
          </div>
        </div>

        {/* Top Performers & Colleges */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Top Performers */}
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
            <h2 className="font-bold text-white mb-4 flex items-center gap-2">
              <Star className="text-amber-400" size={20} />
              Top Performers
            </h2>
            <div className="space-y-3">
              {analytics?.top_performers?.map((performer, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-white/5 rounded-xl">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                    idx === 0 ? 'bg-gradient-to-br from-yellow-400 to-amber-500 text-white' :
                    idx === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-400 text-white' :
                    idx === 2 ? 'bg-gradient-to-br from-amber-600 to-orange-700 text-white' :
                    'bg-gray-700 text-gray-300'
                  }`}>
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-white">{performer.full_name}</p>
                    <p className="text-xs text-gray-500">{performer.college}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-purple-400">{performer.total_score}</p>
                    <p className="text-xs text-gray-500">points</p>
                  </div>
                </div>
              ))}
            </div>
            <Link 
              to="/codearena/rankings"
              className="block text-center text-purple-400 text-sm mt-4 hover:underline"
            >
              View Full Leaderboard →
            </Link>
          </div>

          {/* Top Colleges */}
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
            <h2 className="font-bold text-white mb-4 flex items-center gap-2">
              <GraduationCap className="text-indigo-400" size={20} />
              Top Colleges
            </h2>
            <div className="space-y-3">
              {analytics?.top_colleges?.map((college, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-white/5 rounded-xl">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                    idx === 0 ? 'bg-gradient-to-br from-yellow-400 to-amber-500 text-white' :
                    idx === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-400 text-white' :
                    idx === 2 ? 'bg-gradient-to-br from-amber-600 to-orange-700 text-white' :
                    'bg-gray-700 text-gray-300'
                  }`}>
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-white">{college.college_name}</p>
                    <p className="text-xs text-gray-500">{college.student_count} students</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-indigo-400">{college.avg_score}</p>
                    <p className="text-xs text-gray-500">avg score</p>
                  </div>
                </div>
              ))}
            </div>
            <Link 
              to="/codearena/college-rankings"
              className="block text-center text-indigo-400 text-sm mt-4 hover:underline"
            >
              View All College Rankings →
            </Link>
          </div>
        </div>

        {/* Skills & Score Distribution */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Trending Skills */}
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
            <h2 className="font-bold text-white mb-4 flex items-center gap-2">
              <TrendingUp className="text-cyan-400" size={20} />
              Trending Skills
            </h2>
            <div className="flex flex-wrap gap-2">
              {analytics?.skill_distribution?.map((skill, idx) => (
                <div 
                  key={idx}
                  className="px-4 py-2 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-xl"
                >
                  <span className="text-cyan-400 font-medium">{skill.skill}</span>
                  <span className="text-gray-500 text-sm ml-2">({skill.count})</span>
                </div>
              ))}
            </div>
          </div>

          {/* Score Distribution */}
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
            <h2 className="font-bold text-white mb-4 flex items-center gap-2">
              <BarChart3 className="text-purple-400" size={20} />
              Score Distribution
            </h2>
            <div className="space-y-3">
              {analytics?.score_distribution?.map((bucket, idx) => {
                const maxCount = Math.max(...analytics.score_distribution.map(b => b.count));
                const percentage = maxCount > 0 ? (bucket.count / maxCount) * 100 : 0;
                return (
                  <div key={idx}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-400">{bucket.range}</span>
                      <span className="text-white font-medium">{bucket.count}</span>
                    </div>
                    <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Hiring Stats & Location */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Hiring Stats */}
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
            <h2 className="font-bold text-white mb-4 flex items-center gap-2">
              <Briefcase className="text-emerald-400" size={20} />
              Hiring Statistics
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-blue-500/10 rounded-xl text-center">
                <p className="text-3xl font-bold text-blue-400">{analytics?.hiring_stats?.total_applications || 0}</p>
                <p className="text-sm text-gray-500">Applications</p>
              </div>
              <div className="p-4 bg-green-500/10 rounded-xl text-center">
                <p className="text-3xl font-bold text-green-400">{analytics?.hiring_stats?.total_hired || 0}</p>
                <p className="text-sm text-gray-500">Hired</p>
              </div>
              <div className="p-4 bg-amber-500/10 rounded-xl text-center">
                <p className="text-3xl font-bold text-amber-400">{analytics?.hiring_stats?.total_shortlisted || 0}</p>
                <p className="text-sm text-gray-500">Shortlisted</p>
              </div>
              <div className="p-4 bg-purple-500/10 rounded-xl text-center">
                <p className="text-3xl font-bold text-purple-400">{analytics?.hiring_stats?.hire_rate || 0}%</p>
                <p className="text-sm text-gray-500">Hire Rate</p>
              </div>
            </div>
          </div>

          {/* Location Distribution */}
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
            <h2 className="font-bold text-white mb-4 flex items-center gap-2">
              <MapPin className="text-red-400" size={20} />
              Top Locations
            </h2>
            <div className="space-y-3">
              {analytics?.location_distribution?.slice(0, 6).map((loc, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                  <span className="text-gray-300">{loc.location}</span>
                  <span className="font-bold text-red-400">{loc.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Trending Section */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Upcoming Contests */}
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
            <h2 className="font-bold text-white mb-4 flex items-center gap-2">
              <Calendar className="text-amber-400" size={20} />
              Upcoming Contests
            </h2>
            {trending?.upcoming_contests?.length > 0 ? (
              <div className="space-y-3">
                {trending.upcoming_contests.map((contest, idx) => (
                  <Link
                    key={idx}
                    to={`/codearena/contest/${contest.id}`}
                    className="block p-3 bg-white/5 rounded-xl hover:bg-white/10 transition-colors"
                  >
                    <p className="font-medium text-white">{contest.title}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(contest.start_time).toLocaleDateString()} • {contest.duration_minutes} min
                    </p>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No upcoming contests</p>
            )}
          </div>

          {/* Recent Achievements */}
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
            <h2 className="font-bold text-white mb-4 flex items-center gap-2">
              <Award className="text-yellow-400" size={20} />
              Recent Achievements
            </h2>
            {trending?.recent_achievements?.length > 0 ? (
              <div className="space-y-3">
                {trending.recent_achievements.slice(0, 5).map((ach, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-white/5 rounded-xl">
                    <div className="w-10 h-10 bg-yellow-500/20 rounded-full flex items-center justify-center">
                      <Award className="text-yellow-400" size={20} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-white">{ach.achievement_name}</p>
                      <p className="text-xs text-gray-500">{ach.student_name} • {ach.student_college}</p>
                    </div>
                    <span className="text-xs text-yellow-400">+{ach.points_earned}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No recent achievements</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NationalAnalyticsPage;
